import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  Checkbox,
  TableBody,
  CircularProgress,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { useDispatch, useSelector } from "react-redux";
import { selectCandidateTransferState } from "../../state/candidateTransfer/candidateTransferSelector";
import { getTransferHistories, deleteTransferHistories } from "../../state/candidateTransfer/candidateTransferSlice";
import { format } from "date-fns";

const CandidateTransferTable = () => {
  const dispatch = useDispatch();
  const { histories, isLoadingHistories, isDeletingHistory } = useSelector(selectCandidateTransferState);

  const [selectedHistories, setSelectedHistories] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [deleteConfirmData, setDeleteConfirmData] = useState(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  useEffect(() => {
    dispatch(getTransferHistories());
  }, [dispatch]);

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelected = histories.map((n) => n.id);
      setSelectedHistories(newSelected);
      return;
    }
    setSelectedHistories([]);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selectedHistories.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedHistories, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedHistories.slice(1));
    } else if (selectedIndex === selectedHistories.length - 1) {
      newSelected = newSelected.concat(selectedHistories.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selectedHistories.slice(0, selectedIndex), selectedHistories.slice(selectedIndex + 1));
    }

    setSelectedHistories(newSelected);
  };

  const handleDeleteSelected = () => {
    setDeleteConfirmData(selectedHistories);
    setIsDeleteConfirmOpen(true);
  };

  const handleDeleteSingle = (id) => {
    setDeleteConfirmData([id]);
    setIsDeleteConfirmOpen(true);
  };

  const confirmDelete = () => {
    if (deleteConfirmData && deleteConfirmData.length > 0) {
      dispatch(deleteTransferHistories(deleteConfirmData));

      const remainingSelected = selectedHistories.filter((id) => !deleteConfirmData.includes(id));
      setSelectedHistories(remainingSelected);
    }
    setIsDeleteConfirmOpen(false);
    setDeleteConfirmData(null);
  };

  const cancelDelete = () => {
    setIsDeleteConfirmOpen(false);
    setDeleteConfirmData(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <div>
      <Box mb={2}>
        {selectedHistories.length > 0 && (
          <Button variant="outlined" color="error" onClick={handleDeleteSelected} disabled={isDeletingHistory}>
            Delete Selected ({selectedHistories.length})
          </Button>
        )}
      </Box>
      <TableContainer sx={{ maxHeight: `calc(100vh - 300px)` }}>
        <Table sx={{ minWidth: 650 }}>
          <TableHead sx={{ backgroundColor: "background.default" }}>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  color="primary"
                  indeterminate={selectedHistories.length > 0 && selectedHistories.length < histories.length}
                  checked={histories.length > 0 && selectedHistories.length === histories.length}
                  onChange={handleSelectAllClick}
                />
              </TableCell>
              <TableCell width={120}>Date</TableCell>
              <TableCell width={100}>Type</TableCell>
              <TableCell>From Job</TableCell>
              <TableCell>To Job</TableCell>
              <TableCell width={90}>Count</TableCell>
              <TableCell width={90} align="center">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingHistories ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : histories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3, color: "text.secondary" }}>
                  No transfer/move history yet.
                </TableCell>
              </TableRow>
            ) : (
              histories.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row) => {
                const isItemSelected = selectedHistories.indexOf(row.id) !== -1;
                const createdAt = format(new Date(row.createdAt), "dd MMM yyyy");
                const rowType = row.type === "MOVE" ? "MOVE" : "COPY";
                return (
                  <TableRow key={row.id} hover role="checkbox" aria-checked={isItemSelected} tabIndex={-1} selected={isItemSelected}>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" color="primary" checked={isItemSelected} onChange={(event) => handleClick(event, row.id)} />
                    </TableCell>
                    <TableCell>{createdAt}</TableCell>
                    <TableCell>
                      <Chip label={rowType} color={rowType === "MOVE" ? "secondary" : "primary"} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {row.fromCompanyName}
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        {row.fromJobTitle}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Stage: {row.fromStage}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                        {row.toCompanyName}
                      </Typography>
                      <Typography variant="body2" color="text.primary">
                        {row.toJobTitle}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Stage: Applications
                      </Typography>
                    </TableCell>
                    <TableCell>{row.transferredCount}</TableCell>
                    <TableCell align="center">
                      <IconButton size="small" color="error" onClick={() => handleDeleteSingle(row.id)} disabled={isDeletingHistory}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={histories.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog open={isDeleteConfirmOpen} onClose={cancelDelete} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent dividers>
          <Typography>Are you sure you want to delete {deleteConfirmData?.length > 1 ? `these ${deleteConfirmData.length} records` : "this record"}? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={cancelDelete}>Cancel</Button>
          <Button variant="contained" color="error" onClick={confirmDelete} disabled={isDeletingHistory}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CandidateTransferTable;
